import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { batchAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  X, ChevronUp, ChevronDown, Video, Loader2, CheckCircle2, AlertCircle,
  Minimize2, Maximize2, ExternalLink
} from 'lucide-react';

export const BatchProgressOverlay = () => {
  const [batches, setBatches] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const navigate = useNavigate();

  const pollBatches = useCallback(async () => {
    try {
      const res = await batchAPI.status();
      setBatches(res.data);
    } catch {
      // silent fail
    }
  }, []);

  useEffect(() => {
    pollBatches();
    const interval = setInterval(pollBatches, 3000);
    return () => clearInterval(interval);
  }, [pollBatches]);

  const activeBatches = batches.filter(b => !dismissed.has(b.id));

  if (activeBatches.length === 0) return null;

  const handleDismiss = (id) => {
    setDismissed(prev => new Set([...prev, id]));
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[70vh] flex flex-col"
      data-testid="batch-progress-overlay"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-zinc-900 border border-border/50 rounded-t-xl cursor-pointer"
        onClick={() => setMinimized(!minimized)}
        data-testid="batch-overlay-header"
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-sm font-heading font-semibold">
            Batch Processing ({activeBatches.length})
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => { e.stopPropagation(); navigate('/pipeline'); }}
            data-testid="open-pipeline-button"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
          >
            {minimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Body */}
      {!minimized && (
        <div className="bg-zinc-950 border border-t-0 border-border/50 rounded-b-xl overflow-y-auto max-h-[50vh]">
          {activeBatches.map((batch) => {
            const progress = batch.total > 0 ? Math.round(((batch.completed + batch.failed) / batch.total) * 100) : 0;
            const isTranscription = batch.type === 'transcription';

            return (
              <div key={batch.id} className="p-4 border-b border-border/20 last:border-b-0" data-testid={`batch-item-${batch.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20">
                      {isTranscription ? 'Transcription' : 'Generation'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {batch.completed + batch.failed}/{batch.total}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleDismiss(batch.id)}
                    data-testid={`dismiss-batch-${batch.id}`}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>

                <Progress value={progress} className="h-1.5 mb-3" />

                {/* Current item */}
                {batch.current_video && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin text-primary" />
                    <span className="truncate">{batch.current_video.filename}</span>
                    <Badge variant="secondary" className="text-[9px] ml-auto shrink-0">
                      {batch.current_video.status === 'extracting_audio' ? 'Audio' :
                       batch.current_video.status === 'transcribing' ? 'Transcribing' : 'Processing'}
                    </Badge>
                  </div>
                )}

                {/* Completed/Failed counts */}
                <div className="flex items-center gap-3 mt-2 text-[10px]">
                  {batch.completed > 0 && (
                    <span className="flex items-center gap-1 text-emerald-400">
                      <CheckCircle2 className="w-3 h-3" /> {batch.completed} done
                    </span>
                  )}
                  {batch.failed > 0 && (
                    <span className="flex items-center gap-1 text-red-400">
                      <AlertCircle className="w-3 h-3" /> {batch.failed} failed
                    </span>
                  )}
                </div>

                {/* Video list mini */}
                {batch.videos && (
                  <div className="mt-2 space-y-1 max-h-[120px] overflow-y-auto">
                    {batch.videos.map((v) => (
                      <div key={v.id} className="flex items-center gap-2 text-[10px]">
                        {v.status === 'ready' ? (
                          <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400 shrink-0" />
                        ) : v.status === 'failed' ? (
                          <AlertCircle className="w-2.5 h-2.5 text-red-400 shrink-0" />
                        ) : v.status === 'queued' ? (
                          <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30 shrink-0" />
                        ) : (
                          <Loader2 className="w-2.5 h-2.5 animate-spin text-primary shrink-0" />
                        )}
                        <span className="truncate text-muted-foreground">{v.filename}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
